import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { ARTICLES, CATEGORIES } from "../data/content";
import { Reveal } from "../components/site/Reveal";

const Resources = () => {
  const [category, setCategory] = useState("All");

  const filtered = useMemo(
    () => (category === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === category)),
    [category]
  );
  const featured = filtered.find((a) => a.featured);
  const rest = filtered.filter((a) => a !== featured);

  return (
    <main>
      <Navbar />
      <section className="bg-gradient-to-br from-white via-brand-sky-soft/60 to-white dark:from-brand-ink dark:via-[#0A1030] dark:to-brand-ink pt-36 pb-16 md:pt-44 md:pb-20 grain relative overflow-hidden">
        <div className="absolute -top-24 right-0 w-96 h-96 rounded-full bg-brand-sky/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-4" data-testid="resources-overline">Resources & Blog</p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="font-display text-4xl md:text-6xl font-bold tracking-tighter text-slate-900 dark:text-white max-w-3xl leading-[1.05]" data-testid="resources-headline">
              The evidence behind <span className="text-brand-gradient">human-first documentation</span>
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Research, industry analysis, and practical guides on medical scribing, physician burnout, coding, and
              the real-world performance of AI vs. human documentation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white dark:bg-brand-ink pb-24 md:pb-32 pt-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-2.5 mb-12" role="tablist" aria-label="Article categories" data-testid="category-filters">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setCategory(c)}
                role="tab"
                aria-selected={category === c}
                className={`rounded-full px-5 py-3 text-xs font-bold tracking-wide transition-colors duration-300 ${
                  category === c
                    ? "bg-brand-navy text-white dark:bg-brand-sky dark:text-brand-ink"
                    : "border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-brand-sky hover:text-brand-navy dark:hover:text-brand-sky"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {featured && (
            <Reveal>
              <Link
                to={`/resources/${featured.id}`}
                data-testid="featured-article-card"
                className="group block w-full text-left rounded-[2rem] bg-gradient-to-br from-brand-navy to-brand-navy-deep p-10 md:p-14 mb-8 hover:shadow-[0_40px_90px_-40px_rgba(42,49,146,0.7)] transition-shadow duration-300"
              >
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky mb-4">Featured · {featured.category}</p>
                <h2 className="font-display text-2xl md:text-4xl font-semibold text-white tracking-tight leading-tight max-w-3xl">{featured.title}</h2>
                <p className="mt-4 max-w-2xl text-sm md:text-base text-white/60 leading-relaxed">{featured.excerpt}</p>
                <span className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-brand-sky">
                  Read the article <ArrowRight size={15} className="group-hover:translate-x-1.5 transition-transform duration-300" />
                </span>
              </Link>
            </Reveal>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((a, i) => (
              <Reveal key={a.id} delay={(i % 3) * 0.07}>
                <Link
                  to={`/resources/${a.id}`}
                  data-testid={`article-card-${a.id}`}
                  className="group block w-full h-full text-left rounded-3xl bg-white dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8 flex flex-col hover:shadow-[0_24px_60px_-30px_rgba(42,49,146,0.35)] hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-sky rounded-full bg-brand-sky/10 px-3 py-1.5">{a.category}</span>
                    <ArrowUpRight size={16} className="text-slate-300 dark:text-slate-600 group-hover:text-brand-sky group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[transform,color] duration-300" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white leading-snug">{a.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{a.excerpt}</p>
                  <p className="mt-5 flex items-center gap-2 text-[11px] text-slate-400">
                    <Clock size={11} /> {a.readTime} · {a.date}
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};
export default Resources;
